/**
 * Session management utilities
 * Uses localStorage to store session ID
 * Session data itself is stored in Sanity.io per project rules
 */

const SESSION_ID_KEY = 'laurels_session_id'

/**
 * Generate a unique session ID
 */
function generateSessionId(): string {
  // Use crypto.randomUUID if available, otherwise fallback
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback for environments without crypto.randomUUID
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`
}

/**
 * Get session ID from localStorage
 */
export function getSessionId(): string | null {
  if (typeof window === 'undefined') {
    return null // Server-side rendering
  }

  try {
    return localStorage.getItem(SESSION_ID_KEY)
  } catch (error) {
    console.error('Error reading session ID from localStorage:', error)
    return null
  }
}

/**
 * Set session ID in localStorage
 */
export function setSessionId(sessionId: string): void {
  if (typeof window === 'undefined') {
    return // Server-side rendering
  }

  try {
    localStorage.setItem(SESSION_ID_KEY, sessionId)
  } catch (error) {
    console.error('Error storing session ID in localStorage:', error)
  }
}

/**
 * Get or create session ID
 * Returns existing session ID if present, otherwise creates a new one
 */
export function getOrCreateSessionId(): string {
  let sessionId = getSessionId()
  
  if (!sessionId) {
    sessionId = generateSessionId()
    setSessionId(sessionId)
  }
  
  return sessionId
}

/**
 * Clear session ID from localStorage
 */
export function clearSessionId(): void {
  if (typeof window === 'undefined') {
    return // Server-side rendering
  }

  try {
    localStorage.removeItem(SESSION_ID_KEY)
  } catch (error) {
    console.error('Error clearing session ID from localStorage:', error)
  }
}
